import React from 'react'
import { IEpiProps, IUserSelections, IEventRegistrationRefreshModel, StepConstants } from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext, useEventRegRefreshDispatch } from '../reducers/event-reg-refresh-reducers'
import { Breadcrumbs } from './breadcrumbs'

interface IContextProps {
  epiProps: IEpiProps
  currentStep: string
  hideAttendeesStep: boolean
  userSelections: IUserSelections
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
}

interface BackToDetails {
  backToStepText: string
  backToStep: string
}

// Step navigation (breadcrumbs + back link) under the global site header, which is mounted separately (see layouts/site-chrome)
export const Header = () => {
  const { epiProps, currentStep, hideAttendeesStep, userSelections, eventRegistrationRefreshModel }: IContextProps = useEventRegRefreshContext()!
  const dispatch = useEventRegRefreshDispatch()!

  function navigateToStep(step: string) {
    dispatch({
      type: 'updateCurrentStep',
      data: step,
    })
  }

  const getBackToDetails = (): BackToDetails => {
    if (currentStep == StepConstants.ProgramStep) {
      return {
        backToStepText: "",
        backToStep: ""
      };
    }
    if (currentStep == StepConstants.AddOnsStep) {
      return {
        backToStepText: epiProps.backToProgramSelectionCTAText,
        backToStep: StepConstants.ProgramStep,
      };
    }
    if (currentStep == StepConstants.DateTimeSpecialtyProgramStep) {
      // Check if the selected program has add-ons available
      const selectedProgram = eventRegistrationRefreshModel.specialtyPrograms.find(
        p => p.programCode === userSelections.userProgramSelection?.programCode
          && p.isRenewal === userSelections.userProgramSelection?.isRenewal 
          && p.isDirectDelivery === userSelections.userProgramSelection?.isDirectDelivery
      );
      
      const hasAddOns = selectedProgram &&
        selectedProgram.addOnPrograms &&
        selectedProgram.addOnPrograms.length > 0;

      if (hasAddOns) {
        return {
          backToStepText: epiProps.backToAddOnSelectionCTAText,
          backToStep: StepConstants.AddOnsStep,
        };
      } else {
        return {
          backToStepText: epiProps.backToProgramSelectionCTAText,
          backToStep: StepConstants.ProgramStep,
        };
      }
    }
    if (currentStep == StepConstants.DateTimeAddOnProgramStep) {
      return {
        backToStepText: epiProps.backToDateAndTimeCTAText,
        backToStep: StepConstants.DateTimeSpecialtyProgramStep,
      };
    }
    if (currentStep == StepConstants.AttendeesStep) {
      // If all selected add-ons are product-based, go back to add-ons step (no date/time step was shown)
      const addOnSelections = userSelections.userAddOnProgramSelections || []
      const allAddOnsProductBased = addOnSelections.length > 0 && addOnSelections.every(a => a.disableDateTimeSelection ?? false)
      if (allAddOnsProductBased) {
        return {
          backToStepText: epiProps.backToAddOnSelectionCTAText,
          backToStep: StepConstants.AddOnsStep,
        }
      }
      return {
        backToStepText: epiProps.backToDateAndTimeCTAText,
        backToStep: StepConstants.DateTimeSpecialtyProgramStep,
      };
    }
    if (currentStep == StepConstants.ReviewStep) {
      // Determine the appropriate previous step based on workflow
      let previousStep = StepConstants.DateTimeSpecialtyProgramStep
      let previousStepText = epiProps.backToDateAndTimeCTAText

      // If attendees step is not hidden, that's the previous step
      if (!hideAttendeesStep) {
        previousStep = StepConstants.AttendeesStep
        previousStepText = epiProps.backToAttendeesCTAText
      } 
      // If all add-ons are product-based and attendees step is hidden, go back to add-ons
      else {
        const addOnSelections = userSelections.userAddOnProgramSelections || []
        const allAddOnsProductBased = addOnSelections.length > 0 && addOnSelections.every(a => a.disableDateTimeSelection ?? false)
        if (allAddOnsProductBased) {
          previousStep = StepConstants.AddOnsStep
          previousStepText = epiProps.backToAddOnSelectionCTAText
        }
      }

      return {
        backToStepText: previousStepText,
        backToStep: previousStep,
      };
    }
    
    return {
      backToStepText: "",
      backToStep: ""
    };
  }

  const backToDetails = getBackToDetails();

  return (
    <>
      <div className="app-header">
        <div className="app-header-inner">
          <div className="app-header-top-row">
        <div className="app-header-breadcrumbs-wrapper">
          <Breadcrumbs />
            </div>
          </div>
          </div>
      </div>

      {backToDetails && backToDetails.backToStepText && backToDetails.backToStepText.length > 0 && (
        <div className="app-header-back-section">
          <div className="app-header-inner">
            <button 
              onClick={() => navigateToStep(backToDetails.backToStep)}
              className="app-header-back-link hyperlink"
            >
              <span>{backToDetails.backToStepText}</span>
            </button>
          </div>
        </div>
      )}
    </>
  )
}